import { useEffect, useRef, useState } from "react";
import { PhoneField } from "../../components/PhoneField";
import { useNavigate, Link } from "react-router-dom";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { COMMODITY_CATEGORIES, RESTRICTED_FLAGS, NO_GO_RESTRICTED } from "../../lib/constants";
import { ArrowRight, Plus, Trash, CircleNotch, ShieldCheck, Info } from "@phosphor-icons/react";
import { mapUnavailableReason, LOAD_ASSIST_UNAVAILABLE_TITLE, LOAD_ASSIST_UNAVAILABLE_BODY } from "../../lib/estimateUnavailable";
import { QuoteWeightRateFooter } from "../../components/QuoteWeightRateFooter";
import { AgreementAcceptPanel } from "../../components/legal/AgreementAcceptPanel";
import { slotsForDate, isSlotValid, istToday } from "../../lib/pickupSlots";
import { usePincodeAutofill } from "../../lib/usePincodeAutofill";

const inr = (n) => new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(n);
const inr2 = (n) => new Intl.NumberFormat("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 }).format(n);
const roundOffLabel = (v) => `${Number(v) < 0 ? "− " : "+ "}₹${inr2(Math.abs(Number(v)))}`;
const addrStr = (a) => [a.line1, a.city, a.state].filter((x) => x && String(x).trim()).join(", ");
const emptyLine = () => ({ quantity: "", actual_weight_kg_per_box: "", length_cm: "", width_cm: "", height_cm: "", package_type: "CARTON_BOX" });
export const PACKAGE_TYPES = [
  { value: "CARTON_BOX", label: "Carton / Box" },
  { value: "BAG_SACK", label: "Bag / Sack" },
  { value: "CRATE", label: "Crate" },
  { value: "BUNDLE", label: "Bundle" },
  { value: "ROLL_TUBE", label: "Roll / Tube" },
  { value: "OTHER", label: "Other" },
];
export const LOAD_ASSIST_OPTIONS = [
  { value: "none", label: "No" },
  { value: "pickup", label: "Pickup loading" },
  { value: "delivery", label: "Delivery unloading" },
  { value: "both", label: "Both sides" },
];

export default function NewBooking() {
  const nav = useNavigate();
  const [step, setStep] = useState(1);
  const [busy, setBusy] = useState(false);
  const [shipment, setShipment] = useState(null);
  const [quote, setQuote] = useState(null); // sender-facing auto-quote preview
  const [enquiry, setEnquiry] = useState({ submitting: false, done: false });
  // M3 Slice 7 — company identity selection at acceptance.
  const [company, setCompany] = useState(null);
  const [companyLoading, setCompanyLoading] = useState(false);
  const [companyError, setCompanyError] = useState(null);
  const [gstChoice, setGstChoice] = useState(null); // a company GSTIN id, or "NO_GSTIN"
  // M3 Slice 7 — one idempotency key per acceptance attempt; refreshed whenever a new quote is fetched.
  const acceptanceKeyRef = useRef(null);
  // Promo Code — applied within the Quote commercial flow BEFORE acceptance.
  const appliedPromoRef = useRef(null);
  const [promoInput, setPromoInput] = useState("");
  // Pickup Operating Calendar — backend-authoritative Today/Next-Available choices + slots.
  const [avail, setAvail] = useState(null);
  const [expMsg, setExpMsg] = useState(null);

  const [pickupStale, setPickupStale] = useState(false);
  const [promoBusy, setPromoBusy] = useState(false);
  // M4 — Sender Agreement acceptance state.
  const [agreement, setAgreement] = useState(null);
  const [agreementLoading, setAgreementLoading] = useState(false);
  const [agreementError, setAgreementError] = useState(null);
  const [affirmed, setAffirmed] = useState(false); // Quote-only authority affirmation
  const [agreementBusy, setAgreementBusy] = useState(false);
  const [acceptingPersonName, setAcceptingPersonName] = useState("");

  const [form, setForm] = useState({
    pickup: { line1: "", city: "", state: "", pincode: "", landmark: "" },
    delivery: { line1: "", city: "", state: "", pincode: "", landmark: "" },
    declared_invoice_value: "",
    service_date: "",
    sender_contact_mobile: "",
    receiver: { name: "", company: "", mobile: "", email: "", gstin: "", unloading_contact: "" },
    goods_description: "",
    commodity_category: "GARMENTS",
    preferred_pickup_window: "",
    restricted_goods_declared: [],
    risk_flags: [],
  });

  // Fetch backend availability (Today / Next Available + slots) once the route is known at step 2.
  useEffect(() => {
    if (step !== 2 || !quote?.origin || !quote?.destination) return;
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/booking/pickup-availability`, {
          params: { route_code: `${quote.origin}${quote.destination}` },
        });
        if (!alive) return;
        setAvail(data);
        // Reconcile any existing selection (e.g. resumed Saved Quote) against current choices.
        const choices = [data.today, data.next_available].filter(Boolean);
        const match = choices.find((c) => c.date === form.service_date);
        const slotOk = match && match.slots.includes(form.preferred_pickup_window);
        if (form.service_date && !slotOk) {
          setForm((f) => ({ ...f, service_date: "", preferred_pickup_window: "" }));
          setPickupStale(true);
        }
      } catch (e) { /* availability errors surface at Accept */ }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [step, quote?.origin, quote?.destination]);

  // Fetch the backend-composed expected-delivery message when a date+slot is chosen.
  useEffect(() => {
    if (!avail || !form.service_date || !form.preferred_pickup_window) { setExpMsg(null); return; }
    let alive = true;
    (async () => {
      try {
        const { data } = await api.get(`/booking/pickup-availability`, {
          params: { route_code: `${quote.origin}${quote.destination}`,
                    service_date: form.service_date, slot: form.preferred_pickup_window },
        });
        if (alive) setExpMsg(data.expected_delivery?.message || null);
      } catch (e) { if (alive) setExpMsg(null); }
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [avail, form.service_date, form.preferred_pickup_window]);

  const [lines, setLines] = useState([emptyLine()]);
  const [loadSide, setLoadSide] = useState("none");
  const savedQuoteIdRef = useRef(null);
  const resumeId = new URLSearchParams(window.location.search).get("resume");

  // Resume a Saved Quote: restore all entered booking details, then pricing refreshes on Get Quote.
  useEffect(() => {
    if (!resumeId) return;
    (async () => {
      try {
        const { data } = await api.get(`/booking/saved-quotes/${resumeId}`);
        const cf = data?.inputs?.client_form;
        if (cf) {
          if (cf.form) setForm(cf.form);
          if (Array.isArray(cf.lines) && cf.lines.length) setLines(cf.lines);
          if (cf.loadSide) setLoadSide(cf.loadSide);
        }
        savedQuoteIdRef.current = data.saved_quote_id;
      } catch { toast.error("We couldn't load that saved quote. Please try again."); }
    })();
  }, [resumeId]);

  // Prefill from the public Check Price drawer (sessionStorage), then clear it.
  useEffect(() => {
    try {
      const saved = sessionStorage.getItem("air12_quote_prefill");
      if (saved) {
        const p = JSON.parse(saved);
        setForm((f) => ({
          ...f,
          pickup: { ...f.pickup, line1: p.pickup_address || "", pincode: p.pickup_pincode || "" },
          delivery: { ...f.delivery, line1: p.delivery_address || "", pincode: p.delivery_pincode || "" },
        }));
        if (Array.isArray(p.lines) && p.lines.length) setLines(p.lines);
        if (p.load_assist_side) setLoadSide(p.load_assist_side);
        sessionStorage.removeItem("air12_quote_prefill");
      }
    } catch { /* ignore malformed prefill */ }
  }, []);

  const setLine = (i, k) => (e) => setLines((ls) => ls.map((l, idx) => (idx === i ? { ...l, [k]: e.target.value } : l)));
  const addLine = () => setLines((ls) => [...ls, emptyLine()]);
  const removeLine = (i) => setLines((ls) => (ls.length > 1 ? ls.filter((_, idx) => idx !== i) : ls));

  const validateQuoteInputs = () => {
    for (const [who, a] of [["pickup", form.pickup], ["delivery", form.delivery]]) {
      if (!a.line1.trim() || !a.city.trim() || !a.state.trim() || !a.pincode.trim())
        return `Enter ${who} address line 1, city, state and pincode.`;
    }
    for (const l of lines) {
      const vals = [l.quantity, l.actual_weight_kg_per_box, l.length_cm, l.width_cm, l.height_cm];
      if (vals.some((v) => v === "" || Number(v) <= 0)) return "For every package line, quantity, weight and all dimensions must be greater than zero.";
    }
    return "";
  };

  const onGetQuote = async (promoCode = appliedPromoRef.current) => {
    const v = validateQuoteInputs();
    if (v) return toast.error(v);
    if (!Number(form.declared_invoice_value)) return toast.error("Enter the declared invoice value to generate your insured quote.");
    const _nogo = (form.restricted_goods_declared || []).filter((c) => NO_GO_RESTRICTED.includes(c));
    if (_nogo.length) return toast.error(`Air12 cannot carry the selected goods: ${_nogo.join(", ")}. Please update your goods declaration to continue.`);
    setBusy(true);
    try {
      const payload = {
        pickup_address: addrStr(form.pickup) || form.pickup.line1.trim(),
        pickup_pincode: form.pickup.pincode.trim() || null,
        delivery_address: addrStr(form.delivery) || form.delivery.line1.trim(),
        delivery_pincode: form.delivery.pincode.trim() || null,
        packages: lines.map((l) => ({
          quantity: Number(l.quantity),
          actual_weight_kg_per_box: Number(l.actual_weight_kg_per_box),
          length_cm: Number(l.length_cm),
          width_cm: Number(l.width_cm),
          height_cm: Number(l.height_cm),
          package_type: l.package_type || "CARTON_BOX",
        })),
        load_assist_side: loadSide,
        promo_code: promoCode || null,
        declared_invoice_value: Number(form.declared_invoice_value) || 0,
        saved_quote_id: savedQuoteIdRef.current || null,
        restricted_goods_declared: form.restricted_goods_declared,
        client_form: { form, lines, loadSide },
      };
      const { data } = await api.post("/booking/quote-preview", payload);
      setQuote(data);
      if (data?.saved_quote_id) savedQuoteIdRef.current = data.saved_quote_id;
      setAgreement(null); setAgreementError(null);
      appliedPromoRef.current = data?.promo?.status === "applied" ? promoCode : null;
      // New quote = a new acceptance attempt; issue a fresh idempotency key.
      acceptanceKeyRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (e?.response?.status === 409 && code === "ROUTE_DAY_FULL") {
        const msg = e.response.data.detail.message || "This route is fully booked for today. Please try again tomorrow or request a callback.";
        toast.error(msg);
        setQuote({ status: "route_day_full", message: msg });
      } else {
        setQuote({ status: "manual_review", reason_codes: ["ESTIMATE_UNAVAILABLE"] });
      }
    } finally { setBusy(false); }
  };

  const acceptQuote = () => setStep(2); // advance to shipment details; persistence happens on final accept

  const applyPromo = async () => {
    const c = promoInput.trim().toUpperCase();
    if (!c) return toast.error("Enter a promo code.");
    setPromoBusy(true);
    try { await onGetQuote(c); } finally { setPromoBusy(false); }
  };
  const removePromo = async () => {
    setPromoInput("");
    setPromoBusy(true);
    try { await onGetQuote(null); } finally { setPromoBusy(false); }
  };

  const submitEnquiry = async () => {
    setEnquiry((s) => ({ ...s, submitting: true }));
    try {
      await api.post("/booking/service-enquiry", {
        pickup_address: addrStr(form.pickup) || null,
        pickup_pincode: form.pickup.pincode || null,
        delivery_address: addrStr(form.delivery) || null,
        delivery_pincode: form.delivery.pincode || null,
        inferred_source_airport: quote?.origin || null,
        inferred_destination_airport: quote?.destination || null,
        route_code: (quote?.origin && quote?.destination) ? (quote.origin + quote.destination) : null,
        packages: lines.map((l) => ({
          quantity: Number(l.quantity) || 1,
          actual_weight_kg_per_box: Number(l.actual_weight_kg_per_box) || 0.1,
          length_cm: Number(l.length_cm) || 1,
          width_cm: Number(l.width_cm) || 1,
          height_cm: Number(l.height_cm) || 1,
        })),
        invoice_value: Number(form.declared_invoice_value) || null,
        goods_category: form.commodity_category,
        reason_codes: (quote?.reason_codes && quote.reason_codes.length ? quote.reason_codes : ["PRICE_UNAVAILABLE"]),
        customer_note: "Sender requested Air12 review / callback from booking (price unavailable).",
      });
      setEnquiry({ submitting: false, done: true });
      toast.success("Enquiry received — Air12 will get back to you.");
    } catch (e) {
      setEnquiry({ submitting: false, done: false });
      toast.error(e?.displayMessage || "Could not submit enquiry");
    }
  };

  const onAcceptAndCreate = async () => {
    if (!quote || quote.status !== "estimate_available") return toast.error("Get an Air12 quote first");
    if (!/^[0-9]{10}$/.test(form.sender_contact_mobile)) return toast.error("Sender contact mobile must be exactly 10 digits");
    if (!form.receiver.name || !form.receiver.mobile || !form.receiver.email) return toast.error("Receiver name, mobile and email are required");
    if (!/^[0-9]{10}$/.test(form.receiver.mobile)) return toast.error("Receiver mobile must be exactly 10 digits");
    if (!form.goods_description || !Number(form.declared_invoice_value)) return toast.error("Goods description and invoice value are required");
    if (!form.service_date) return toast.error("Select a Service Date for pickup");
    if (form.service_date < istToday()) return toast.error("Service date cannot be in the past.");
    // Final IST recheck of the pickup time immediately before booking creation. A same-day slot that
    // was valid when selected but has since gone stale is cleared and re-selection is required (never
    // auto-advanced). Backend independently re-validates against server IST.
    if (!isSlotValid(form.service_date, form.preferred_pickup_window)) {
      setForm((f) => ({ ...f, preferred_pickup_window: "" }));
      if (form.preferred_pickup_window && slotsForDate(form.service_date).length === 0)
        return toast.error("No pickup times remain today. Please choose a later service date.");
      return toast.error("The selected pickup time is no longer valid. Please choose a new pickup time.");
    }
    if (!gstChoice) return toast.error("Select a GST registration or No GSTIN for this shipment");
    if (!agreement?.company_accepted) return toast.error("Your company must accept the Air12 Sender Agreement for this quote first.");
    if (!affirmed) return toast.error("Please confirm you are authorised to accept this Quote on behalf of your company.");
    if (!/^[A-Za-z. ]+$/.test(acceptingPersonName.trim())) return toast.error("Enter the authorised person's full name using letters, spaces and periods only.");
    // Safety net: never send a blank key. Reuse the current attempt's key across retries; only mint
    // one here if somehow absent (backend rejects a missing key).
    if (!acceptanceKeyRef.current) acceptanceKeyRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`);
    setBusy(true);
    try {
      const payload = {
        saved_quote_id: savedQuoteIdRef.current || null,
        receiver: {
          name: form.receiver.name,
          company: form.receiver.company || null,
          mobile: form.receiver.mobile,
          email: form.receiver.email,
          delivery_address: form.delivery,
          gstin: form.receiver.gstin || null,
          unloading_contact: form.receiver.unloading_contact || null,
        },
        pickup_address: form.pickup,
        sender_contact_mobile: form.sender_contact_mobile,
        packages: lines.map((l) => ({
          quantity: Number(l.quantity),
          actual_weight_kg_per_box: Number(l.actual_weight_kg_per_box),
          length_cm: Number(l.length_cm),
          width_cm: Number(l.width_cm),
          height_cm: Number(l.height_cm),
          package_type: l.package_type || "CARTON_BOX",
        })),
        goods_description: form.goods_description,
        commodity_category: form.commodity_category,
        declared_invoice_value: Number(form.declared_invoice_value),
        service_date: form.service_date,
        preferred_pickup_window: form.preferred_pickup_window || null,
        restricted_goods_declared: form.restricted_goods_declared,
        risk_flags: form.risk_flags,
        client_quoted_payable: quote.payable, // complete insurance-inclusive firm total (authoritative)
        promo_code: appliedPromoRef.current || null, // server re-resolves + re-freezes the same discount
        load_assist_side: loadSide,
        company_gstin_id: gstChoice && gstChoice !== "NO_GSTIN" ? gstChoice : null,
        no_gstin: gstChoice === "NO_GSTIN",
        acceptance_key: acceptanceKeyRef.current,
        // Quote-only acceptance (the Sender Agreement is a separate, prior company-level acceptance).
        quote_authority_affirmed: !!affirmed,
        accepting_person_name: acceptingPersonName.trim(),
      };
      const { data } = await api.post("/booking/accept-quote", payload, { timeout: 90000 });
      setShipment(data);
      toast.success(`Booking ${data.public_shipment_number} created`);
      setStep(3);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.code === "QUOTE_CHANGED") {
        toast.error("The Air12 service price changed. Please review the updated quote.");
        if (detail.new_quote) { setQuote(detail.new_quote); acceptanceKeyRef.current = (window.crypto?.randomUUID?.() || `${Date.now()}-${Math.random()}`); }
        setStep(1);
      } else if (e?.response?.status === 409 && detail?.code === "ROUTE_DAY_FULL") {
        toast.error(detail.message || "This route is fully booked for today. Please try again tomorrow or request a callback.");
        setStep(1);
        setQuote({ status: "route_day_full", message: detail.message });
      } else if (e?.response?.status === 409 && detail?.code === "AGREEMENT_ACCEPTANCE_REQUIRED") {
        toast.error("Your company must accept the Air12 Sender Agreement for this quote before accepting the quote.");
        await loadAgreement();
      } else if (e?.response?.status === 422 && detail?.code === "QUOTE_AFFIRMATION_REQUIRED") {
        toast.error("Please confirm you are authorised to accept this Quote on behalf of your company.");
      } else if (e?.response?.status === 422 && ["PICKUP_TIME_STALE", "PICKUP_TIME_NO_SAME_DAY_SLOT", "SERVICE_DATE_PAST"].includes(detail?.code)) {
        // Server IST rejected the pickup slot as stale/exhausted/past — clear it and force re-selection.
        setForm((f) => ({ ...f, preferred_pickup_window: "" }));
        toast.error(detail?.code === "PICKUP_TIME_STALE"
          ? "The selected pickup time is no longer valid. Please choose a new pickup time."
          : (detail?.message || "Please choose a valid pickup time."));
      } else if (e?.response?.status === 422 && ["PICKUP_TIME_REQUIRED", "PICKUP_TIME_INVALID"].includes(detail?.code)) {
        toast.error(detail?.message || "Select a valid pickup time.");
      } else if (e?.response?.status === 502 && detail?.code === "AGREEMENT_GENERATION_FAILED") {
        toast.error(detail.message || "Your Quote Acceptance Record could not be generated. Your quote was not accepted — please try again.");
      } else if (detail?.code && ["AGREEMENT_TEMPLATE_UNAVAILABLE", "ACCEPTANCE_RECORD_TEMPLATE_UNAVAILABLE", "LEGAL_ENTITY_UNAVAILABLE"].includes(detail.code)) {
        toast.error(detail.message || "The Sender Agreement is not available right now. Please try again shortly.");
      } else if (e?.response?.status === 422) {
        toast.error(detail?.message || "This shipment needs Air12 review and cannot be auto-booked.");
      } else {
        toast.error(e.displayMessage || "Could not create booking");
      }
    } finally { setBusy(false); }
  };

  const isQuoteOk = quote && quote.status === "estimate_available";

  const loadCompany = async () => {
    setCompanyLoading(true); setCompanyError(null);
    try {
      const { data } = await api.get("/company");
      setCompany(data);
    } catch (e) {
      setCompanyError(e.displayMessage || "We couldn’t load your company identity.");
    } finally { setCompanyLoading(false); }
  };
  useEffect(() => { if (step === 2 && !company && !companyLoading && !companyError) loadCompany(); }, [step]); // eslint-disable-line

  const loadAgreement = async () => {
    setAgreementLoading(true); setAgreementError(null);
    try {
      if (!savedQuoteIdRef.current) throw new Error("Please return to the quote screen and refresh your quote.");
      const { data } = await api.get("/booking/agreement/current", { params: { saved_quote_id: savedQuoteIdRef.current } });
      setAgreement(data);
    } catch (e) {
      setAgreementError(e.displayMessage || e.message || "We couldn’t load the Air12 Sender Agreement.");
    } finally { setAgreementLoading(false); }
  };

  // Company-level acceptance of the Agreement version PINNED to this quote (distinct from the Quote).
  const acceptAgreement = async (acceptingName) => {
    setAgreementBusy(true);
    try {
      await api.post("/booking/agreement/accept", {
        agreement_version_id: agreement.version_id, authority_affirmed: true,
        accepting_person_name: acceptingName, saved_quote_id: savedQuoteIdRef.current,
      }, { timeout: 90000 });
      setAgreement((a) => ({ ...a, company_accepted: true }));
      toast.success("Air12 Sender Agreement accepted for your company.");
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (detail?.code === "AGREEMENT_VERSION_STALE") { toast.error(detail.message); await loadAgreement(); }
      else toast.error(detail?.message || "The Sender Agreement could not be accepted. Please retry.");
    } finally { setAgreementBusy(false); }
  };
  useEffect(() => { if (step === 2 && !agreement && !agreementLoading && !agreementError) loadAgreement(); }, [step]); // eslint-disable-line

  const downloadDoc = async (kind) => {
    try {
      const res = await api.get(`/booking/${shipment.shipment_id}/agreement/document/${kind}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      window.open(url, "_blank");
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(e.displayMessage || "Could not open the document. Please try again.");
    }
  };
  const unavailable = mapUnavailableReason(quote ? quote.reason_codes : []);

  return (
    <div className="max-w-[1100px] mx-auto p-6" data-testid="new-booking-page">
      <div className="mb-4 text-xs"><a href="/sender" className="underline">← My shipments</a></div>
      <div className="overline">NEW BOOKING</div>
      <h1 className="heading text-3xl mb-1">
        Step {step} of 3 · {step === 1 ? "Air12 service quote" : step === 2 ? "Shipment details" : "Packages"}
      </h1>
      <p className="text-sm text-[hsl(var(--muted))] mb-6">
        Air12 detects the nearest cargo terminal from your addresses and auto-generates your service price. IDs, labels and QR codes are generated server-side.
      </p>

      {/* STEP 1 — Auto-generated Air12 service quote */}
      {step === 1 && (
        <div className="space-y-6">
          <Section title="PICKUP ADDRESS">
            <AddressFields v={form.pickup} onChange={(pickup) => setForm({ ...form, pickup })} prefix="pickup" />
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-3">
              <Field label="Sender Contact Mobile">
                <PhoneField value={form.sender_contact_mobile} onChange={(v) => setForm({ ...form, sender_contact_mobile: v })} testid="sender-contact-mobile-input" />
              </Field>
            </div>
            <p className="text-xs text-[hsl(var(--muted))] mt-2">Operational contact for this shipment. Enter exactly 10 digits (India, +91).</p>
          </Section>
          <Section title="DELIVERY ADDRESS">
            <AddressFields v={form.delivery} onChange={(delivery) => setForm({ ...form, delivery })} prefix="delivery" />
          </Section>

          <Section title="PACKAGE DETAILS">
            <div className="space-y-3">
              {lines.map((l, i) => (
                <div key={i} className="border border-[hsl(var(--border))] rounded-[2px] p-3" data-testid={`pkgline-${i}`}>
                  <div className="flex items-center justify-between mb-2">
                    <div className="text-xs font-medium text-[hsl(var(--muted))]">Package type {i + 1}</div>
                    {lines.length > 1 && (
                      <button type="button" onClick={() => removeLine(i)} className="text-[hsl(var(--muted))] hover:text-[hsl(var(--destructive))]" data-testid={`pkgline-${i}-remove`} aria-label="Remove package type"><Trash size={14} /></button>
                    )}
                  </div>
                  <div className="grid grid-cols-2 md:grid-cols-5 gap-2">
                    <Field label="Weight/box (kg)"><input type="number" min="0" step="0.1" className="input-field" value={l.actual_weight_kg_per_box} onChange={setLine(i, "actual_weight_kg_per_box")} placeholder="20" data-testid={`pkgline-${i}-weight`} /></Field>
                    <Field label="Quantity"><input type="number" min="1" className="input-field" value={l.quantity} onChange={setLine(i, "quantity")} placeholder="5" data-testid={`pkgline-${i}-qty`} /></Field>
                    <Field label="L (cm)"><input type="number" min="0" className="input-field" value={l.length_cm} onChange={setLine(i, "length_cm")} placeholder="60" data-testid={`pkgline-${i}-l`} /></Field>
                    <Field label="W (cm)"><input type="number" min="0" className="input-field" value={l.width_cm} onChange={setLine(i, "width_cm")} placeholder="40" data-testid={`pkgline-${i}-w`} /></Field>
                    <Field label="H (cm)"><input type="number" min="0" className="input-field" value={l.height_cm} onChange={setLine(i, "height_cm")} placeholder="40" data-testid={`pkgline-${i}-h`} /></Field>
                    <Field label="Package type">
                      <select className="input-field" value={l.package_type} onChange={setLine(i, "package_type")} data-testid={`pkgline-${i}-type`}>
                        {PACKAGE_TYPES.map((t) => <option key={t.value} value={t.value}>{t.label}</option>)}
                      </select>
                    </Field>
                  </div>
                </div>
              ))}
            </div>
            <button type="button" className="btn-ghost mt-3" onClick={addLine} data-testid="add-package-type-btn"><Plus size={12} /> Add another package type</button>
          </Section>

          <Section title="INVOICE">
            <Field label="Declared invoice value (₹)"><input type="number" className="input-field" value={form.declared_invoice_value} onChange={(e) => setForm({ ...form, declared_invoice_value: e.target.value })} data-testid="invoice-value-input" /></Field>
          </Section>

          <Section title="LOADING & UNLOADING ASSISTANCE">
            <div data-testid="load-assist-selector">
              <div className="text-xs text-[hsl(var(--muted))] mb-2">Need loading / unloading assistance? (optional add-on)</div>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
                {LOAD_ASSIST_OPTIONS.map((opt) => (
                  <button
                    key={opt.value}
                    type="button"
                    onClick={() => { setLoadSide(opt.value); setQuote(null); }}
                    className={`text-sm rounded-[2px] border px-3 py-2 text-left transition-colors ${
                      loadSide === opt.value
                        ? "border-[hsl(var(--accent))] bg-[hsl(var(--info-bg))] font-medium"
                        : "border-[hsl(var(--border))] hover:border-[hsl(var(--border-strong))]"
                    }`}
                    data-testid={`load-assist-option-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            </div>
          </Section>

          {!quote && (
            <div className="flex justify-end">
              <button className="btn-primary" onClick={() => onGetQuote()} disabled={busy} data-testid="get-quote-button">
                {busy ? (<><CircleNotch size={14} className="animate-spin" /> Calculating…</>) : (<>Get Air12 quote <ArrowRight size={14} /></>)}
              </button>
            </div>
          )}

          {/* Auto-generated quote result */}
          {isQuoteOk && (
            <Section title="YOUR AIR12 SERVICE QUOTE">
              <div data-testid="quote-result">
                <div className="overline">Firm price (incl. insurance &amp; GST)</div>
                <div className="heading text-3xl tracking-tight mt-1" data-testid="quote-firm-price">₹{inr(quote.payable)}</div>

                <dl className="mt-5 divide-y divide-[hsl(var(--border))] text-sm">
                  <Row k="Route detected" v={quote.route_label} testid="quote-route" />
                  <Row k="Air12 service charge" v={`₹${inr(quote.air12_service_charge ?? quote.air12_service_subtotal)}`} testid="quote-service-charge" />
                  {quote.load_assist && quote.load_assist.available && quote.load_assist.pickup && quote.load_assist.pickup.selected && (
                <Row k={quote.load_assist.pickup.label} v={`₹${inr(quote.load_assist.pickup.amount)}`} testid="quote-load-assist-pickup" />
                  )}
                  {quote.load_assist && quote.load_assist.available && quote.load_assist.delivery && quote.load_assist.delivery.selected && (
                <Row k={quote.load_assist.delivery.label} v={`₹${inr(quote.load_assist.delivery.amount)}`} testid="quote-load-assist-delivery" />
                  )}
                  {quote.promo && quote.promo.status === "applied" && (
                    <Row k={`Promo (${quote.promo.code})`} v={`− ₹${inr(quote.promo.discount)}`} testid="promo-discount-line" />
                  )}
                  <Row k="Insurance" v={`₹${inr(quote.insurance_premium)}`} testid="quote-insurance" />
                  <Row k="Taxable subtotal" v={`₹${inr(quote.taxable_subtotal)}`} testid="quote-taxable-subtotal" />
                  <Row k={`GST (${quote.gst_rate}%)`} v={`₹${inr2(quote.gst_amount)}`} testid="quote-gst" />
                  {Number(quote.round_off) !== 0 && (
                    <Row k="Round Off" v={roundOffLabel(quote.round_off)} testid="quote-round-off" />
                  )}
                  <Row k="Total payable" v={`₹${inr(quote.payable)}`} testid="quote-total-payable" />
                </dl>

                {/* Promo Code — apply / remove / replace before acceptance */}
                <div className="mt-4" data-testid="promo-section">
                  <div className="overline mb-1">Promo code</div>
                  {quote.promo && quote.promo.status === "applied" ? (
                    <div className="flex items-center justify-between gap-2 text-sm bg-[hsl(var(--info-bg))] px-3 py-2 rounded-[2px]">
                      <span data-testid="promo-applied-label">
                        <strong>{quote.promo.code}</strong> applied — ₹{inr(quote.promo.discount)} off
                      </span>
                      <button className="btn-ghost text-sm" onClick={removePromo} disabled={promoBusy} data-testid="promo-remove-button">
                        Remove
                      </button>
                    </div>
                  ) : (
                    <div className="flex gap-2">
                      <input
                        className="input-field"
                        value={promoInput}
                        onChange={(e) => setPromoInput(e.target.value.toUpperCase())}
                        placeholder="Letters, numbers and hyphens only"
                        data-testid="promo-input"
                      />
                      <button className="btn-ghost text-sm whitespace-nowrap" onClick={applyPromo} disabled={promoBusy} data-testid="promo-apply-button">
                        {promoBusy ? "Applying…" : "Apply"}
                      </button>
                    </div>
                  )}
                  {quote.promo && quote.promo.status === "rejected" && (
                    <p className="text-xs text-red-600 mt-1" data-testid="promo-error">{quote.promo.message}</p>
                  )}
                </div>

                <QuoteWeightRateFooter
                  physical={quote.total_actual_weight_kg}
                  volumetric={quote.total_volumetric_weight_kg}
                  chargeable={quote.total_chargeable_weight_kg}
                  billable={quote.billable_weight_kg}
                  taxableSubtotal={quote.taxable_subtotal}
                />

                {quote.load_assist && quote.load_assist.available === false && (
                  <div className="text-xs text-[hsl(var(--muted))] leading-relaxed mt-3 flex items-start gap-1.5" data-testid="load-assist-unavailable">
                    <Info size={14} weight="bold" className="mt-0.5 shrink-0" />
                    <span><strong className="text-[hsl(var(--ink))]">{LOAD_ASSIST_UNAVAILABLE_TITLE}.</strong> {LOAD_ASSIST_UNAVAILABLE_BODY}</span>
                  </div>
                )}

                <div className="mt-4 flex items-start gap-2 text-xs text-[hsl(var(--accent))] bg-[hsl(var(--info-bg))] px-3 py-2 rounded-[2px]" data-testid="insurance-note">
                  <ShieldCheck size={14} weight="bold" className="mt-0.5 shrink-0" />
                  <span><strong>Cargo insurance is included in your Air12 total.</strong> Air12 adds mandatory cargo insurance to your booking (before GST) — there is no separate insurance form or payment for you.</span>
                </div>
                <p className="text-xs text-[hsl(var(--muted))] leading-relaxed mt-3">{quote.disclaimer}</p>

                <div className="flex justify-between mt-5">
                  <button className="btn-ghost" onClick={() => setQuote(null)} data-testid="recalc-quote-button">Edit & recalculate</button>
                  <button className="btn-primary" onClick={acceptQuote} data-testid="accept-quote-button">Continue <ArrowRight size={14} /></button>
                </div>
              </div>
            </Section>
          )}

          {quote && quote.status === "route_day_full" && (
            <Section title="ROUTE FULLY BOOKED TODAY">
              <div className="text-center py-2" data-testid="quote-route-day-full">
                <div className="grid place-items-center w-12 h-12 mx-auto bg-[hsl(var(--info-bg))] text-[hsl(var(--accent))] rounded-[2px]"><Info size={24} weight="bold" /></div>
                <h3 className="heading text-xl tracking-tight mt-4" data-testid="quote-route-day-full-title">This route is fully booked for today</h3>
                <p className="text-sm text-[hsl(var(--muted))] leading-relaxed mt-3 max-w-md mx-auto" data-testid="quote-route-day-full-message">
                  {quote.message || "This route is fully booked for today. Please try again tomorrow or request a callback."}
                </p>
                <div className="flex justify-center gap-2 mt-4">
                  <button className="btn-ghost" onClick={() => setQuote(null)} data-testid="route-day-full-return-button">Return to booking</button>
                  {enquiry.done ? (
                    <span className="text-sm text-[hsl(var(--accent))] inline-flex items-center gap-1.5" data-testid="route-day-full-enquiry-done"><ShieldCheck size={14} weight="bold" /> Callback requested</span>
                  ) : (
                    <button className="btn-primary" disabled={enquiry.submitting} onClick={submitEnquiry} data-testid="route-day-full-callback-button">
                      {enquiry.submitting ? (<><CircleNotch size={13} className="animate-spin" /> Submitting…</>) : "Request a callback"}
                    </button>
                  )}
                </div>
              </div>
            </Section>
          )}

          {quote && !isQuoteOk && quote.status !== "route_day_full" && (
            <Section title="INSTANT ESTIMATE NOT AVAILABLE">
              <div className="text-center py-2" data-testid="quote-unavailable">
                <div className="grid place-items-center w-12 h-12 mx-auto bg-[hsl(var(--info-bg))] text-[hsl(var(--accent))] rounded-[2px]"><Info size={24} weight="bold" /></div>
                <h3 className="heading text-xl tracking-tight mt-4" data-testid="quote-unavailable-title">{unavailable.title}</h3>
                <p className="text-sm text-[hsl(var(--muted))] leading-relaxed mt-3 max-w-md mx-auto" data-testid="quote-unavailable-message">
                  {quote.customer_message || unavailable.body}
                </p>

                {/* Service Enquiry — explicit "request a callback" lead (Module 7.2E4). Persisted only on click. */}
                <div className="mt-5 max-w-md mx-auto" data-testid="booking-service-enquiry-block">
                  {enquiry.done ? (
                    <p className="text-sm text-[hsl(var(--accent))] flex items-center justify-center gap-1.5" data-testid="booking-service-enquiry-done">
                      <ShieldCheck size={14} weight="bold" /> Enquiry received — Air12 will review and get back to you.
                    </p>
                  ) : (
                    <button className="btn-primary" disabled={enquiry.submitting} onClick={submitEnquiry} data-testid="booking-request-callback-btn">
                      {enquiry.submitting ? (<><CircleNotch size={13} className="animate-spin" /> Submitting…</>) : (unavailable.cta)}
                    </button>
                  )}
                  <button className="btn-ghost mt-3 ml-3" onClick={() => setQuote(null)} data-testid="recalc-quote-button">{unavailable.secondary}</button>
                </div>
              </div>
            </Section>
          )}
        </div>
      )}

      {/* STEP 2 — Shipment details (route locked from the accepted quote; no manual airport choice) */}
      {step === 2 && isQuoteOk && (
        <div className="space-y-6">
          <div className="grid-card grid-card-strong p-4">
            <div className="overline">DETECTED ROUTE</div>
            <div className="id-chip id-chip-strong mt-1 text-base" data-testid="detected-route">{quote.route_label} · ₹{inr(quote.payable)}</div>
            <p className="text-xs text-[hsl(var(--muted))] mt-2">Air12 detected this route from your addresses. Your booking and final quote are created when you accept below.</p>
          </div>

          <Section title="PICKUP">
            <Field label="Pickup date (required)">
              <div className="flex flex-col gap-2" data-testid="pickup-date-choices">
                {avail && [avail.today, avail.next_available].filter(Boolean).map((c) => (
                  <label key={c.date}
                    className={`flex items-center gap-2 rounded-md border p-2 cursor-pointer text-sm ${form.service_date === c.date ? "border-[hsl(var(--primary))] bg-[hsl(var(--primary)/0.06)]" : "border-[hsl(var(--border))]"}`}>
                    <input type="radio" name="pickup-date" value={c.date}
                      checked={form.service_date === c.date}
                      onChange={() => { setPickupStale(false); setForm({ ...form, service_date: c.date, preferred_pickup_window: "" }); }}
                      data-testid={`pickup-date-${c.date}`} />
                    <span>{c.label}</span>
                  </label>
                ))}
                {!avail && <p className="text-xs text-[hsl(var(--muted))]">Loading pickup availability…</p>}
              </div>
              {pickupStale && (
                <p className="text-xs text-red-600 mt-2" data-testid="pickup-stale-notice">
                  Your previous pickup time is no longer available. Please select a new pickup time.
                </p>
              )}
            </Field>
            <Field label="Preferred pickup time (required)">
              <select className="input-field" value={form.preferred_pickup_window}
                disabled={!form.service_date}
                onChange={(e) => setForm({ ...form, preferred_pickup_window: e.target.value })}
                data-testid="pickup-time-select">
                <option value="">{form.service_date ? "Select a pickup time" : "Select a pickup date first"}</option>
                {(() => {
                  const c = avail && [avail.today, avail.next_available].filter(Boolean).find((x) => x.date === form.service_date);
                  return (c ? c.slots : []).map((s) => (<option key={s} value={s}>{s}</option>));
                })()}
              </select>
            </Field>
            {expMsg && (
              <p className="text-xs text-[hsl(var(--muted))] mt-2" data-testid="pickup-expected-delivery">{expMsg}</p>
            )}
            <p className="text-xs text-[hsl(var(--muted))] mt-2">Pickup times are in India Standard Time (IST). Air12 checks route capacity for this date. Mandatory cargo insurance is included in your Air12 total — there is no separate insurance step or payment.</p>
          </Section>

          <Section title="RECEIVER">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Name"><input className="input-field" value={form.receiver.name} onChange={(e) => setForm({ ...form, receiver: { ...form.receiver, name: e.target.value } })} data-testid="receiver-name-input" /></Field>
              <Field label="Company (optional)"><input className="input-field" value={form.receiver.company} onChange={(e) => setForm({ ...form, receiver: { ...form.receiver, company: e.target.value } })} /></Field>
              <Field label="Mobile"><PhoneField value={form.receiver.mobile} onChange={(v) => setForm({ ...form, receiver: { ...form.receiver, mobile: v } })} testid="receiver-mobile-input" /></Field>
              <Field label="Email"><input type="email" className="input-field" value={form.receiver.email} onChange={(e) => setForm({ ...form, receiver: { ...form.receiver, email: e.target.value } })} data-testid="receiver-email-input" /></Field>
            </div>
            <p className="text-xs text-[hsl(var(--muted))] mt-2">Delivery address: {addrStr(form.delivery)} — {form.delivery.pincode}</p>
          </Section>

          <Section title="GOODS">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              <Field label="Description"><input className="input-field" value={form.goods_description} onChange={(e) => setForm({ ...form, goods_description: e.target.value })} data-testid="goods-description-input" /></Field>
              <Field label="Commodity category">
                <select className="input-field" value={form.commodity_category} onChange={(e) => setForm({ ...form, commodity_category: e.target.value })} data-testid="commodity-select">
                  {COMMODITY_CATEGORIES.map((c) => <option key={c} value={c}>{c.replace(/_/g, " ")}</option>)}
                </select>
              </Field>
            </div>
            <div className="overline mt-3 mb-1.5">RESTRICTED GOODS DECLARATION</div>
            <p className="text-xs text-[hsl(var(--muted))] mb-2">Tick any of these only if the shipment contains them. Some may require additional Air12 checks or may not be accepted.</p>
            <div className="flex flex-wrap gap-2">
              {RESTRICTED_FLAGS.map((f) => (
                <label key={f} className="inline-flex items-center gap-1.5 text-xs border px-2 py-1 rounded-sm cursor-pointer">
                  <input type="checkbox" checked={form.restricted_goods_declared.includes(f)} onChange={(e) => {
                    const next = e.target.checked ? [...form.restricted_goods_declared, f] : form.restricted_goods_declared.filter((x) => x !== f);
                    setForm({ ...form, restricted_goods_declared: next });
                  }} data-testid={`restricted-${f}`} />
                  {f.replace(/_/g, " ")}
                </label>
              ))}
            </div>
          </Section>

          <Section title="AIR12 SENDER AGREEMENT">
            {agreement?.company_accepted ? (
              <div className="text-sm text-[hsl(var(--muted))]" data-testid="agreement-already-accepted">
                Your company has accepted the Air12 Sender Agreement (v{agreement.version}) that governs this quote.
              </div>
            ) : (
              <AgreementAcceptPanel agreement={agreement} loading={agreementLoading} error={agreementError}
                                    onRetry={loadAgreement} onAccept={acceptAgreement} busy={agreementBusy}
                                    testIdPrefix="agreement" />
            )}
          </Section>

          <Section title="COMPANY IDENTITY FOR THIS SHIPMENT">
            {companyLoading ? (
              <div className="text-sm text-[hsl(var(--muted))]" data-testid="company-identity-loading">Loading company identity…</div>
            ) : companyError ? (
              <div className="text-sm text-red-600" data-testid="company-identity-error">
                {companyError} <button className="underline" onClick={loadCompany} data-testid="company-identity-retry">Retry</button>
              </div>
            ) : company ? (
              <div className="space-y-3" data-testid="company-identity-section">
                <div className="grid sm:grid-cols-2 gap-3">
                  <div><div className="overline text-[10px] mb-1">Company legal name</div><div className="text-sm font-semibold" data-testid="ci-legal-name">{company.legal_name}</div></div>
                  <div><div className="overline text-[10px] mb-1">Company PAN</div><div className="font-mono text-sm" data-testid="ci-pan">{company.pan_masked}</div></div>
                  <div className="sm:col-span-2"><div className="overline text-[10px] mb-1">Company address</div><div className="text-sm whitespace-pre-line" data-testid="ci-address">{typeof company.registered_address === "string" ? company.registered_address : JSON.stringify(company.registered_address)}</div></div>
                </div>
                <div>
                  <div className="overline text-[10px] mb-1.5">GST registration</div>
                  <div className="space-y-1.5" data-testid="gst-choices">
                    {(company.gstins || []).filter((g) => (g.status || "ACTIVE") === "ACTIVE").map((g) => (
                      <label key={g.id} className="flex items-center gap-2 border rounded-sm px-3 py-2 cursor-pointer text-sm" data-testid={`gst-choice-${g.id}`}>
                        <input type="radio" name="gst" checked={gstChoice === g.id} onChange={() => setGstChoice(g.id)} data-testid={`gst-radio-${g.id}`} />
                        <span className="font-mono">{g.gstin_masked}</span>
                      </label>
                    ))}
                    <label className="flex items-center gap-2 border rounded-sm px-3 py-2 cursor-pointer text-sm" data-testid="gst-choice-none">
                      <input type="radio" name="gst" checked={gstChoice === "NO_GSTIN"} onChange={() => setGstChoice("NO_GSTIN")} data-testid="gst-radio-none" />
                      <span>No GSTIN</span>
                    </label>
                  </div>
                  <p className="text-[11px] text-[hsl(var(--muted))] mt-2">
                    Need to change your company name, address, or add a GSTIN? Update it on <Link to="/sender/company" className="underline" data-testid="ci-company-link">Company details</Link>, then return here.
                  </p>
                </div>
              </div>
            ) : (
              <div className="text-sm text-[hsl(var(--muted))]" data-testid="company-identity-unavailable">Company identity unavailable.</div>
            )}
          </Section>

          {agreement?.company_accepted && company && (
            <Section title="ACCEPT THIS QUOTE">
              <div className="space-y-3" data-testid="quote-acceptance-section">
                <div className="space-y-1" data-testid="accepting-person-block">
                  <label className="text-sm font-medium" data-testid="accepting-person-name-label">Full legal name of the authorised person accepting this Quote</label>
                  <input type="text" className="input-field w-full" value={acceptingPersonName}
                         onChange={(e) => setAcceptingPersonName(e.target.value.replace(/[^A-Za-z. ]/g, ""))}
                         placeholder="e.g. A. B. Sharma" autoComplete="off" data-testid="accepting-person-name-input" />
                </div>
                <label className="flex items-start gap-2 cursor-pointer text-sm" data-testid="quote-affirm-label">
                  <input type="checkbox" className="mt-0.5" checked={affirmed} onChange={(e) => setAffirmed(e.target.checked)} data-testid="quote-affirm-checkbox" />
                  <span>I confirm that I am authorised to accept this Quote on behalf of {company.legal_name}.</span>
                </label>
              </div>
            </Section>
          )}

          <div className="flex justify-between">
            <button className="btn-ghost" onClick={() => setStep(1)} data-testid="back-to-quote-button">← Back to quote</button>
            <button className="btn-primary" onClick={onAcceptAndCreate} disabled={busy || !gstChoice || !company || !agreement?.company_accepted || !affirmed || !/^[A-Za-z. ]+$/.test(acceptingPersonName.trim())} data-testid="create-shipment-button">
              {busy ? "Accepting quote…" : "Accept Quote"} <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}

      {/* STEP 3 — Booking created (shipment + auto-quote + labels generated server-side) */}
      {step === 3 && shipment && (
        <div className="space-y-6">
          <div className="grid-card grid-card-strong p-5 text-center" data-testid="booking-success">
            <div className="grid place-items-center w-12 h-12 mx-auto bg-[hsl(var(--info-bg))] text-[hsl(var(--accent))] rounded-[2px]"><ShieldCheck size={24} weight="bold" /></div>
            <div className="overline mt-4">BOOKING CREATED</div>
            <div className="id-chip id-chip-strong mt-1 text-base" data-testid="created-shipment-number">{shipment.public_shipment_number}</div>
            <p className="text-sm text-[hsl(var(--muted))] mt-3">
              {shipment.route_label} · {shipment.package_count} package{shipment.package_count === 1 ? "" : "s"} · Air12 service ₹{inr(shipment.quote?.payable)}
            </p>
            <p className="text-xs text-[hsl(var(--muted))] mt-1" data-testid="draft-awaiting-payment-hint">Draft — Awaiting Payment. Printable labels and documents are generated after you pay and complete the final declaration.</p>
          </div>

          <Section title="YOUR AIR12 SERVICE QUOTE (FIRM)">
            <dl className="divide-y divide-[hsl(var(--border))] text-sm" data-testid="success-quote-breakdown">
              <Row k="Quote ID" v={shipment.quote?.public_quote_id} />
              <Row k="Chargeable weight" v={`${shipment.quote?.total_chargeable_weight_kg} kg`} />
              {Number(shipment.quote?.billable_weight_kg) > Number(shipment.quote?.total_chargeable_weight_kg) && (
                <Row k="Billable weight" v={`${shipment.quote?.billable_weight_kg} kg`} testid="success-billable-weight" />
              )}
              <Row k="Air12 service charge" v={`₹${inr(shipment.quote?.air12_service_charge ?? shipment.quote?.subtotal_excl_insurance)}`} testid="success-service-charge" />
              {shipment.quote?.load_assist && shipment.quote.load_assist.available && shipment.quote.load_assist.pickup?.selected && (
                <Row k={shipment.quote.load_assist.pickup.label} v={`₹${inr(shipment.quote.load_assist.pickup.amount)}`} testid="success-load-assist-pickup" />
              )}
              {shipment.quote?.load_assist && shipment.quote.load_assist.available && shipment.quote.load_assist.delivery?.selected && (
                <Row k={shipment.quote.load_assist.delivery.label} v={`₹${inr(shipment.quote.load_assist.delivery.amount)}`} testid="success-load-assist-delivery" />
              )}
              <Row k="Cargo insurance (included)" v={`₹${inr(shipment.quote?.insurance_premium ?? 0)}`} testid="success-insurance-line" />
              <Row k={`GST (${shipment.quote?.gst_rate}%)`} v={`₹${inr2(shipment.quote?.gst_amount)}`} />
              {Number(shipment.quote?.round_off) !== 0 && (
                <Row k="Round Off" v={roundOffLabel(shipment.quote?.round_off)} testid="success-round-off" />
              )}
              <Row k="Total payable (incl. insurance & GST)" v={`₹${inr(shipment.quote?.payable)}`} testid="success-total-payable" strong />
            </dl>
            <p className="text-[11px] text-[hsl(var(--muted))] mt-2" data-testid="success-payable-note">
              Includes mandatory cargo insurance and GST. Pay this single Air12 total — there is no separate insurance payment.
            </p>
          </Section>

          {shipment.company_identity && (
            <Section title="COMPANY IDENTITY (FROZEN FOR THIS SHIPMENT)">
              <dl className="divide-y divide-[hsl(var(--border))] text-sm" data-testid="accepted-company-identity">
                <Row k="Company legal name" v={shipment.company_identity.company_legal_name} />
                <Row k="Company PAN" v={shipment.company_identity.pan_masked} />
                <Row k="GST registration" v={shipment.company_identity.no_gstin ? "No GSTIN" : shipment.company_identity.gstin_masked} testid="accepted-gstin" />
              </dl>
              <p className="text-[11px] text-[hsl(var(--muted))] mt-2">These company details are frozen for this shipment. Later changes on Company details do not affect it.</p>
            </Section>
          )}

          {shipment.quote_acceptance?.accepted && (
            <Section title="LEGAL DOCUMENTS">
              <p className="text-[12px] text-[hsl(var(--muted))] mb-3" data-testid="agreement-accepted-note">
                Quote accepted ({shipment.quote_acceptance.record_id}). Download your Quote Acceptance Record and the governing Air12 Sender Agreement below.
              </p>
              <div className="flex flex-wrap gap-3">
                <button className="btn-ghost" onClick={() => downloadDoc("agreement")} data-testid="agreement-download-sender-agreement">
                  Air12 Sender Agreement (PDF)
                </button>
                <button className="btn-ghost" onClick={() => downloadDoc("acceptance_record")} data-testid="agreement-download-acceptance-record">
                  Quote Acceptance Record (PDF)
                </button>
              </div>
            </Section>
          )}

          <div className="flex justify-between">
            <button className="btn-ghost" onClick={() => nav("/sender")} data-testid="go-to-shipments-button">← My shipments</button>
            <button className="btn-primary" onClick={() => nav(`/sender/shipments/${shipment.shipment_id}`)} data-testid="view-shipment-button">
              View shipment to pay <ArrowRight size={14} />
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

export function Section({ title, children }) {
  return (
    <section className="grid-card">
      <div className="px-4 py-3 border-b"><div className="overline">{title}</div></div>
      <div className="p-4">{children}</div>
    </section>
  );
}

export function Field({ label, children, wide }) {
  return (
    <div className={wide ? "md:col-span-2" : ""}>
      <div className="overline mb-1.5">{label}</div>
      {children}
    </div>
  );
}

export function Row({ k, v, testid, strong }) {
  return (
    <div className="flex items-center justify-between py-2" data-testid={testid}>
      <dt className={strong ? "font-semibold text-[hsl(var(--fg))]" : "text-[hsl(var(--muted))]"}>{k}</dt>
      <dd className={`text-right ${strong ? "font-semibold" : "font-medium"}`}>{v}</dd>
    </div>
  );
}

export function AddressFields({ v, onChange, prefix }) {
  const upd = (k, val) => onChange({ ...v, [k]: val });
  const { status, error, onPincodeChange } = usePincodeAutofill(v, onChange);
  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
      <Field label="Line 1" wide><input className="input-field" value={v.line1} onChange={(e) => upd("line1", e.target.value)} data-testid={`${prefix}-line1`} /></Field>
      <Field label="Landmark (opt.)"><input className="input-field" value={v.landmark || ""} onChange={(e) => upd("landmark", e.target.value)} /></Field>
      <Field label="Pincode">
        <input className="input-field" inputMode="numeric" maxLength={6} value={v.pincode} onChange={(e) => onPincodeChange(e.target.value)} data-testid={`${prefix}-pincode`} />
        {status === "resolving" && <p className="text-xs text-[hsl(var(--muted))] mt-1" data-testid={`${prefix}-pin-resolving`}>Resolving City/State…</p>}
        {status === "error" && <p className="text-xs text-[hsl(var(--destructive))] mt-1" data-testid={`${prefix}-pin-error`}>{error}</p>}
      </Field>
      <Field label="City"><input className="input-field opacity-70 cursor-not-allowed" value={v.city} readOnly tabIndex={-1} placeholder="Auto from PIN" data-testid={`${prefix}-city`} /></Field>
      <Field label="State"><input className="input-field opacity-70 cursor-not-allowed" value={v.state} readOnly tabIndex={-1} placeholder="Auto from PIN" data-testid={`${prefix}-state`} /></Field>
    </div>
  );
}
